import React, { useRef, useState } from 'react';
import { fmtRequestDay } from '../../../store/stockRequestStore';

interface ExpectedDateInputProps {
  value: string;
  onChange: (isoDate: string) => void;
  className?: string;
  style?: React.CSSProperties;
  mutedColor?: string;
  textColor?: string;
  hoverBg?: string;
  /** Compact table cell mode — fills the table cell hit area */
  compact?: boolean;
  /** Force the hover highlight on (e.g. multi-select linked cells) */
  forceHover?: boolean;
  onHoverChange?: (hovered: boolean) => void;
  onMouseDown?: (e: React.MouseEvent) => void;
}

/**
 * Date cell that displays dd/mm/yyyy and opens the native picker on click.
 * Values are stored as yyyy-mm-dd.
 */
const ExpectedDateInput: React.FC<ExpectedDateInputProps> = ({
  value,
  onChange,
  className = '',
  style,
  mutedColor,
  textColor,
  hoverBg = 'rgba(255,255,255,0.06)',
  compact = true,
  forceHover = false,
  onHoverChange,
  onMouseDown,
}) => {
  const [hovered, setHovered] = useState(false);
  const pickerRef = useRef<HTMLInputElement>(null);

  const setHover = (next: boolean) => {
    setHovered(next);
    onHoverChange?.(next);
  };

  const openPicker = () => {
    const el = pickerRef.current;
    if (!el) return;
    try {
      el.showPicker?.();
    } catch {
      el.click();
    }
  };

  const lit = hovered || forceHover;

  return (
    <div
      role="button"
      tabIndex={0}
      aria-label="Expected delivery date"
      className={
        compact
          ? `flex items-center min-w-0 h-full w-full cursor-pointer transition-colors outline-none focus-visible:ring-1 focus-visible:ring-blue-500/50 ${className}`
          : `flex items-center min-w-0 w-full cursor-pointer transition-colors outline-none rounded focus-visible:ring-1 focus-visible:ring-blue-500/50 ${className}`
      }
      style={{
        ...style,
        backgroundColor: lit ? hoverBg : (style?.backgroundColor ?? 'transparent'),
      }}
      onMouseDown={onMouseDown}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={openPicker}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          openPicker();
        }
      }}
    >
      <span
        className={`truncate leading-none ${compact ? 'text-sm' : 'text-xs'}`}
        style={{ color: value ? (textColor ?? style?.color) : (mutedColor ?? style?.color) }}
      >
        {value ? fmtRequestDay(value) : ''}
      </span>
      <input
        ref={pickerRef}
        type="date"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          opacity: 0,
          pointerEvents: 'none',
          width: 0,
          height: 0,
          border: 0,
          padding: 0,
        }}
        tabIndex={-1}
        aria-hidden
      />
    </div>
  );
};

export default ExpectedDateInput;
